import React, { useState } from 'react';
import { ClientPortalLayout } from './ClientPortalLayout';
import { FileTextIcon, VideoIcon, ImageIcon, PresentationIcon, DownloadIcon, SearchIcon, FilterIcon } from 'lucide-react';
export function DeliverablesCenterPage() {
  const [activeFilter, setActiveFilter] = useState('All');
  const stats = [{
    label: 'Documents',
    count: 12,
    icon: FileTextIcon,
    color: 'text-blue-500',
    bg: 'bg-blue-50'
  }, {
    label: 'Videos',
    count: 4,
    icon: VideoIcon,
    color: 'text-purple-500',
    bg: 'bg-purple-50'
  }, {
    label: 'Photos',
    count: 156,
    icon: ImageIcon,
    color: 'text-emerald-500',
    bg: 'bg-emerald-50'
  }, {
    label: 'Decks',
    count: 3,
    icon: PresentationIcon,
    color: 'text-amber-500',
    bg: 'bg-amber-50'
  }];
  const deliverables = [{
    title: 'Executive Diagnostic Report',
    type: 'PDF',
    category: 'Pre-Event',
    date: 'Oct 15, 2026',
    size: '2.4 MB',
    icon: FileTextIcon
  }, {
    title: 'Pre-Read Strategy Memo',
    type: 'PDF',
    category: 'Pre-Event',
    date: 'Oct 18, 2026',
    size: '1.1 MB',
    icon: FileTextIcon
  }, {
    title: 'Day 1 Opening Keynote',
    type: 'Video',
    category: 'During Event',
    date: 'Oct 22, 2026',
    size: '450 MB',
    icon: VideoIcon
  }, {
    title: 'Q4 Pipeline Presentation',
    type: 'Deck',
    category: 'During Event',
    date: 'Oct 23, 2026',
    size: '15 MB',
    icon: PresentationIcon
  }, {
    title: 'Group Photos (Day 2)',
    type: 'Album',
    category: 'During Event',
    date: 'Oct 24, 2026',
    size: '1.2 GB',
    icon: ImageIcon
  }, {
    title: 'Outcome Action Plan',
    type: 'PDF',
    category: 'Post-Event',
    date: 'Oct 28, 2026',
    size: '3.5 MB',
    icon: FileTextIcon
  }, {
    title: '30-Day Follow-Up Survey',
    type: 'Form',
    category: 'Post-Event',
    date: 'Nov 25, 2026',
    size: 'Link',
    icon: FileTextIcon
  }];
  const filtered = activeFilter === 'All' ? deliverables : deliverables.filter(d => d.category === activeFilter);
  return <ClientPortalLayout data-id="element-324">
      <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-8" data-id="element-325">
        <div data-id="element-326">
          <h1 className="font-serif-display text-3xl text-slate-900 mb-2" data-id="element-327">
            Deliverables Center
          </h1>
          <p className="text-sm text-slate-500" data-id="element-328">
            Access all your pre-read materials, event assets, and post-event
            reports.
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-329">
          {stats.map((stat, i) => <div key={i} className="bg-white rounded-2xl border border-slate-200 p-5 shadow-sm flex items-center gap-4" data-id="element-330">
              <div className={`w-12 h-12 rounded-xl ${stat.bg} ${stat.color} flex items-center justify-center shrink-0`} data-id="element-331">
                <stat.icon className="w-6 h-6" data-id="element-332" />
              </div>
              <div data-id="element-333">
                <div className="text-2xl font-serif-display text-slate-900" data-id="element-334">
                  {stat.count}
                </div>
                <div className="text-xs font-medium text-slate-500 uppercase tracking-wider" data-id="element-335">
                  {stat.label}
                </div>
              </div>
            </div>)}
        </div>

        {/* List Section */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden" data-id="element-336">
          {/* Toolbar */}
          <div className="p-4 border-b border-slate-200 flex flex-col sm:flex-row justify-between items-center gap-4 bg-slate-50/50" data-id="element-337">
            <div className="flex items-center gap-2 w-full sm:w-auto" data-id="element-338">
              {['All', 'Pre-Event', 'During Event', 'Post-Event'].map(filter => <button key={filter} onClick={() => setActiveFilter(filter)} className={`px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${activeFilter === filter ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-500 hover:text-slate-700 hover:bg-slate-100'}`} data-id="element-339">
                    {filter}
                  </button>)}
            </div>

            <div className="relative w-full sm:w-64" data-id="element-340">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-341" />
              <input type="text" placeholder="Search files..." className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 outline-none" data-id="element-342" />
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-id="element-343">
            <table className="w-full text-left border-collapse" data-id="element-344">
              <thead data-id="element-345">
                <tr className="border-b border-slate-200 bg-slate-50/50" data-id="element-346">
                  <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-347">
                    Name
                  </th>
                  <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-348">
                    Category
                  </th>
                  <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-349">
                    Date
                  </th>
                  <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-350">
                    Size
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-bold text-slate-500 uppercase tracking-wider" data-id="element-351">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100" data-id="element-352">
                {filtered.map((item, i) => <tr key={i} className="hover:bg-slate-50 transition-colors group" data-id="element-353">
                    <td className="px-6 py-4" data-id="element-354">
                      <div className="flex items-center gap-3" data-id="element-355">
                        <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center text-slate-500 shrink-0" data-id="element-356">
                          <item.icon className="w-4 h-4" data-id="element-357" />
                        </div>
                        <div data-id="element-358">
                          <div className="text-sm font-bold text-slate-900" data-id="element-359">
                            {item.title}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-360">
                            {item.type}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-361">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-700" data-id="element-362">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-363">
                      {item.date}
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600" data-id="element-364">
                      {item.size}
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-365">
                      <button className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors opacity-0 group-hover:opacity-100" data-id="element-366">
                        <DownloadIcon className="w-4 h-4" data-id="element-367" />
                      </button>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </ClientPortalLayout>;
}